/* assets/src/viewer/materials/textures.js
 * Libreria texture procedurali (port :271-599) con COLOR SPACE corretti:
 *  - mappe colore (map)      -> SRGBColorSpace
 *  - mappe dati (bump)       -> NoColorSpace (default, lineare)
 * Le texture sono condivise (userData.asgShared) e MAI disposte dal DisposalManager.
 */
import * as THREE from 'three';
import { performanceMode } from '../settings.js';

let library = null;

function makeRepeatTexture(renderer, canvas, repeatX = 1, repeatY = 1, colorSpace = THREE.NoColorSpace) {
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.colorSpace = colorSpace;
  texture.needsUpdate = true;
  const maxAniso = renderer && renderer.capabilities && renderer.capabilities.getMaxAnisotropy
    ? renderer.capabilities.getMaxAnisotropy() : 1;
  texture.anisotropy = Math.min(performanceMode() ? 2 : 8, maxAniso);
  texture.userData = texture.userData || {};
  texture.userData.asgShared = true;
  return texture;
}

function createTexture(renderer, { size = 256, repeatX = 1, repeatY = 1, colorSpace = THREE.NoColorSpace, painter }) {
  const canvas = document.createElement('canvas');
  const textureSize = performanceMode() ? Math.max(128, Math.min(size, 384)) : size;
  canvas.width = textureSize;
  canvas.height = textureSize;
  const ctx = canvas.getContext('2d');
  painter(ctx, textureSize);
  return makeRepeatTexture(renderer, canvas, repeatX, repeatY, colorSpace);
}

const SRGB = THREE.SRGBColorSpace;

export function buildProceduralTextureLibrary(renderer) {
  if (library) return library;

  const metalMap = createTexture(renderer, {
    size: 768, repeatX: 6, repeatY: 4, colorSpace: SRGB,
    painter: (ctx, size) => {
      const grad = ctx.createLinearGradient(0, 0, size, 0);
      grad.addColorStop(0, '#5f6773'); grad.addColorStop(0.18, '#8f98a6'); grad.addColorStop(0.45, '#cdd4dd');
      grad.addColorStop(0.7, '#808997'); grad.addColorStop(1, '#5a6370');
      ctx.fillStyle = grad; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 3200; i++) {
        const x = Math.random() * size; const alpha = 0.025 + Math.random() * 0.06; const tone = 140 + Math.random() * 85;
        ctx.fillStyle = `rgba(${tone}, ${tone}, ${tone}, ${alpha})`; ctx.fillRect(x, 0, 1, size);
      }
      for (let i = 0; i < 120; i++) {
        const y = Math.random() * size; const h = 1 + Math.random() * 2;
        ctx.fillStyle = `rgba(255,255,255,${0.015 + Math.random() * 0.025})`; ctx.fillRect(0, y, size, h);
      }
    },
  });

  const metalBump = createTexture(renderer, {
    size: 768, repeatX: 6, repeatY: 4,
    painter: (ctx, size) => {
      ctx.fillStyle = '#808080'; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 3400; i++) {
        const x = Math.random() * size; const tone = Math.random() > 0.5 ? 220 : 30;
        ctx.fillStyle = `rgba(${tone}, ${tone}, ${tone}, ${0.02 + Math.random() * 0.05})`; ctx.fillRect(x, 0, 1, size);
      }
    },
  });

  const deckMap = createTexture(renderer, {
    size: 1024, repeatX: 2.2, repeatY: 2.2, colorSpace: SRGB,
    painter: (ctx, size) => {
      const plankW = size / 7;
      ctx.fillStyle = '#9c6d43'; ctx.fillRect(0, 0, size, size);
      for (let p = 0; p < 7; p++) {
        const x = p * plankW; const base = ['#b57d4f', '#9e6b42', '#c18b57', '#8f5f3a'][p % 4];
        ctx.fillStyle = base; ctx.fillRect(x, 0, plankW, size);
        ctx.fillStyle = 'rgba(40,22,10,0.36)'; ctx.fillRect(x + plankW - 4, 0, 4, size);
        ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fillRect(x + 1, 0, 2, size);
        for (let i = 0; i < 240; i++) {
          const y = Math.random() * size; const thickness = 1 + Math.random() * 4; const alpha = 0.04 + Math.random() * 0.1; const shade = 55 + Math.random() * 60;
          ctx.fillStyle = `rgba(${shade}, ${shade * 0.62}, ${shade * 0.35}, ${alpha})`; ctx.fillRect(x, y, plankW, thickness);
        }
        for (let i = 0; i < 24; i++) {
          const cx = x + plankW * (0.22 + Math.random() * 0.56); const cy = Math.random() * size; const rx = 10 + Math.random() * 26; const ry = 4 + Math.random() * 11;
          ctx.strokeStyle = 'rgba(70,38,16,0.22)'; ctx.lineWidth = 1; ctx.beginPath();
          ctx.ellipse(cx, cy, rx, ry, Math.random() * Math.PI, 0, Math.PI * 2); ctx.stroke();
        }
      }
    },
  });

  const deckBump = createTexture(renderer, {
    size: 1024, repeatX: 2.2, repeatY: 2.2,
    painter: (ctx, size) => {
      ctx.fillStyle = '#808080'; ctx.fillRect(0, 0, size, size);
      const plankW = size / 7;
      for (let p = 0; p < 7; p++) {
        const x = p * plankW; ctx.fillStyle = 'rgba(35,35,35,0.6)'; ctx.fillRect(x + plankW - 4, 0, 4, size);
        for (let i = 0; i < 260; i++) {
          const y = Math.random() * size; const h = 1 + Math.random() * 3; const tone = 90 + Math.random() * 110;
          ctx.fillStyle = `rgba(${tone}, ${tone}, ${tone}, ${0.06 + Math.random() * 0.08})`; ctx.fillRect(x, y, plankW, h);
        }
      }
    },
  });

  const woodMap = createTexture(renderer, {
    size: 768, repeatX: 3, repeatY: 3, colorSpace: SRGB,
    painter: (ctx, size) => {
      ctx.fillStyle = '#7e5537'; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 420; i++) {
        const y = Math.random() * size; const h = 1 + Math.random() * 2; const shade = 40 + Math.random() * 80;
        ctx.fillStyle = `rgba(${shade + 50}, ${shade + 15}, ${shade * 0.55}, ${0.06 + Math.random() * 0.08})`; ctx.fillRect(0, y, size, h);
      }
      for (let i = 0; i < 24; i++) {
        const cx = Math.random() * size; const cy = Math.random() * size;
        ctx.strokeStyle = 'rgba(58,28,10,0.24)'; ctx.lineWidth = 1; ctx.beginPath();
        ctx.ellipse(cx, cy, 10 + Math.random() * 18, 3 + Math.random() * 8, Math.random() * Math.PI, 0, Math.PI * 2); ctx.stroke();
      }
    },
  });

  const woodBump = createTexture(renderer, {
    size: 768, repeatX: 3, repeatY: 3,
    painter: (ctx, size) => {
      ctx.fillStyle = '#7d7d7d'; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 520; i++) {
        const y = Math.random() * size;
        ctx.fillStyle = `rgba(${110 + Math.random() * 90}, ${110 + Math.random() * 90}, ${110 + Math.random() * 90}, ${0.04 + Math.random() * 0.08})`;
        ctx.fillRect(0, y, size, 1 + Math.random() * 2);
      }
    },
  });

  const fabricMap = createTexture(renderer, {
    size: 512, repeatX: 4, repeatY: 4, colorSpace: SRGB,
    painter: (ctx, size) => {
      ctx.fillStyle = '#d8d1c8'; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < size; i += 5) {
        ctx.strokeStyle = 'rgba(255,255,255,0.07)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, size); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(size, i); ctx.stroke();
      }
      for (let i = 0; i < 1100; i++) {
        const x = Math.random() * size; const y = Math.random() * size; const alpha = 0.02 + Math.random() * 0.04; const tone = 175 + Math.random() * 60;
        ctx.fillStyle = `rgba(${tone}, ${tone}, ${tone - 8}, ${alpha})`; ctx.fillRect(x, y, 2, 2);
      }
    },
  });

  const fabricBump = createTexture(renderer, {
    size: 512, repeatX: 4, repeatY: 4,
    painter: (ctx, size) => {
      ctx.fillStyle = '#7f7f7f'; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < size; i += 5) {
        ctx.strokeStyle = 'rgba(255,255,255,0.08)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, size); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(size, i); ctx.stroke();
      }
    },
  });

  const grassMap = createTexture(renderer, {
    size: 1024, repeatX: 3, repeatY: 3, colorSpace: SRGB,
    painter: (ctx, size) => {
      const base = ctx.createLinearGradient(0, 0, 0, size);
      base.addColorStop(0, '#295d22'); base.addColorStop(0.5, '#3d7a31'); base.addColorStop(1, '#214a1d');
      ctx.fillStyle = base; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 14000; i++) {
        const x = Math.random() * size; const y = Math.random() * size; const h = 4 + Math.random() * 12; const sway = -2 + Math.random() * 4;
        ctx.strokeStyle = ['rgba(110,180,78,0.34)', 'rgba(72,135,55,0.34)', 'rgba(161,210,115,0.2)', 'rgba(48,96,39,0.28)'][Math.floor(Math.random() * 4)];
        ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, y + h); ctx.quadraticCurveTo(x + sway, y + h * 0.5, x + sway * 1.4, y); ctx.stroke();
      }
      for (let i = 0; i < 1800; i++) {
        const x = Math.random() * size; const y = Math.random() * size;
        ctx.fillStyle = `rgba(90,60,32,${0.02 + Math.random() * 0.05})`; ctx.fillRect(x, y, 3, 2);
      }
    },
  });

  const grassBump = createTexture(renderer, {
    size: 1024, repeatX: 3, repeatY: 3,
    painter: (ctx, size) => {
      ctx.fillStyle = '#6f6f6f'; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 12000; i++) {
        const x = Math.random() * size; const y = Math.random() * size; const h = 4 + Math.random() * 10; const sway = -2 + Math.random() * 4; const tone = 110 + Math.random() * 120;
        ctx.strokeStyle = `rgba(${tone}, ${tone}, ${tone}, ${0.08 + Math.random() * 0.08})`;
        ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, y + h); ctx.quadraticCurveTo(x + sway, y + h * 0.5, x + sway * 1.35, y); ctx.stroke();
      }
    },
  });

  const plasterMap = createTexture(renderer, {
    size: 768, repeatX: 1.3, repeatY: 1.1, colorSpace: SRGB,
    painter: (ctx, size) => {
      ctx.fillStyle = '#ede6dc'; ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 2600; i++) {
        const x = Math.random() * size; const y = Math.random() * size; const tone = 210 + Math.random() * 30;
        ctx.fillStyle = `rgba(${tone}, ${tone - 4}, ${tone - 7}, ${0.015 + Math.random() * 0.05})`; ctx.fillRect(x, y, 2, 2);
      }
    },
  });

  library = {
    metal: { map: metalMap, bump: metalBump },
    deck: { map: deckMap, bump: deckBump },
    wood: { map: woodMap, bump: woodBump },
    fabric: { map: fabricMap, bump: fabricBump },
    grass: { map: grassMap, bump: grassBump },
    plaster: { map: plasterMap },
  };
  return library;
}
